import {
  createAsyncThunk,
  createSlice,
  createSelector,
  PayloadAction,
} from "@reduxjs/toolkit"
import type { RootState } from "@/store"
import type { EmployeeDto } from "../dtos"
import type { EmployeeDetailsDto } from "../dtos/employee-details.dto"
import { onboardingService } from "@/modules/onboarding/infrastructure/onboarding-service"

function getErrorMessage(err: unknown, fallback: string) {
  if (typeof err === "object" && err !== null) {
    const maybeResponse = (err as { response?: unknown }).response
    if (typeof maybeResponse === "object" && maybeResponse !== null) {
      const maybeData = (maybeResponse as { data?: unknown }).data
      if (typeof maybeData === "object" && maybeData !== null) {
        const maybeMessage = (maybeData as { message?: unknown }).message
        if (typeof maybeMessage === "string") return maybeMessage
      }
    }
  }

  if (err instanceof Error && err.message) return err.message
  return fallback
}

interface EmployeesState {
  items: EmployeeDto[]
  selectedEmployee: EmployeeDto | null
  employeeDetails: EmployeeDetailsDto | null
  isLoading: boolean
  isLoadingDetails: boolean
  error: string | null
}

const initialState: EmployeesState = {
  items: [],
  selectedEmployee: null,
  employeeDetails: null,
  isLoading: false,
  isLoadingDetails: false,
  error: null,
}

export const fetchEmployeesThunk = createAsyncThunk<
  EmployeeDto[],
  { searchTerm?: string; onboardingStatus?: string } | void,
  { rejectValue: string }
>("employees/fetchAll", async (arg, { rejectWithValue }) => {
  try {
    const searchTerm = arg && typeof arg === "object" ? arg.searchTerm : undefined
    const onboardingStatus = arg && typeof arg === "object" ? arg.onboardingStatus : undefined
    return await onboardingService.getEmployees(searchTerm, onboardingStatus)
  } catch (err: unknown) {
    return rejectWithValue(getErrorMessage(err, "Failed to load employees"))
  }
})

export const createEmployeeThunk = createAsyncThunk<
  EmployeeDto,
  FormData,
  { rejectValue: string }
>("employees/create", async (formData, { rejectWithValue }) => {
  try {
    return await onboardingService.createEmployee(formData)
  } catch (err: unknown) {
    return rejectWithValue(getErrorMessage(err, "Failed to create employee"))
  }
})

export const updateEmployeeThunk = createAsyncThunk<
  EmployeeDto,
  {
    id: string
    payload: {
      firstName: string
      lastName: string
      email: string
      jobTitle: string
    }
  },
  { rejectValue: string }
>("employees/update", async ({ id, payload }, { rejectWithValue }) => {
  try {
    return await onboardingService.updateEmployee(id, payload)
  } catch (err: unknown) {
    return rejectWithValue(getErrorMessage(err, "Failed to update employees"))
  }
})

export const deleteEmployeeThunk = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>("employees/delete", async (id, { rejectWithValue }) => {
  try {
    await onboardingService.deleteEmployee(id)
    return id
  } catch (err: unknown) {
    return rejectWithValue(getErrorMessage(err, "Failed to delete employees"))
  }
})

export const inviteEmployeeThunk = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>("employees/invite", async (id, { rejectWithValue }) => {
  try {
    await onboardingService.inviteEmployee(id)
    return id
  } catch (err: unknown) {
    return rejectWithValue(getErrorMessage(err, "Failed to send invite"))
  }
})

export const fetchEmployeeDetailsThunk = createAsyncThunk<
  EmployeeDetailsDto,
  string,
  { rejectValue: string }
>("employees/fetchDetails", async (id, { rejectWithValue }) => {
  try {
    return await onboardingService.getEmployeeDetails(id)
  } catch (err: unknown) {
    return rejectWithValue(
      getErrorMessage(err, "Failed to load employee details"),
    )
  }
})

export const acceptEmployeeThunk = createAsyncThunk<
  void,
  string,
  { rejectValue: string }
>("employees/accept", async (id, { rejectWithValue }) => {
  try {
    await onboardingService.acceptEmployee(id)
  } catch (err: unknown) {
    return rejectWithValue(getErrorMessage(err, "Failed to accept employee"))
  }
})

export const rejectEmployeeThunk = createAsyncThunk<
  void,
  { id: string; notes: string },
  { rejectValue: string }
>("employees/reject", async ({ id, notes }, { rejectWithValue }) => {
  try {
    await onboardingService.rejectEmployee(id, notes)
  } catch (err: unknown) {
    return rejectWithValue(getErrorMessage(err, "Failed to reject employee"))
  }
})

export const flagEmployeeSectionsThunk = createAsyncThunk<
  void,
  { onboardingId: string; flags: { fieldName: string; note: string }[] },
  { rejectValue: string }
>(
  "employees/flagSections",
  async ({ onboardingId, flags }, { rejectWithValue }) => {
    try {
      await onboardingService.flagEmployeeSections(onboardingId, flags)
    } catch (err: unknown) {
      return rejectWithValue(getErrorMessage(err, "Failed to flag sections"))
    }
  },
)

const employeeSlice = createSlice({
  name: "employees",
  initialState,
  reducers: {
    setEmployees(state, action: PayloadAction<EmployeeDto[]>) {
      state.items = action.payload
    },
    setSelectedEmployee(state, action: PayloadAction<EmployeeDto | null>) {
      state.selectedEmployee = action.payload
    },
    setEmployeesLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload
    },
    setEmployeesError(state, action: PayloadAction<string | null>) {
      state.error = action.payload
    },
    clearEmployeesError(state) {
      state.error = null
    },
    clearEmployeesState() {
      return initialState
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmployeesThunk.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchEmployeesThunk.fulfilled, (state, action) => {
        state.isLoading = false
        state.items = action.payload
      })
      .addCase(fetchEmployeesThunk.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload ?? "Failed to load employees"
      })
      .addCase(createEmployeeThunk.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(createEmployeeThunk.fulfilled, (state, action) => {
        state.isLoading = false
        state.items = [action.payload, ...state.items]
      })
      .addCase(createEmployeeThunk.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload ?? "Failed to onboard employee"
      })
      .addCase(updateEmployeeThunk.fulfilled, (state, action) => {
        state.items = state.items.map((e) =>
          e.id === action.payload.id ? action.payload : e,
        )
        if (state.selectedEmployee?.id === action.payload.id) {
          state.selectedEmployee = action.payload
        }
      })
      .addCase(deleteEmployeeThunk.fulfilled, (state, action) => {
        state.items = state.items.filter((e) => e.id !== action.payload)
        if (state.selectedEmployee?.id === action.payload) {
          state.selectedEmployee = null
        }
      })
      .addCase(fetchEmployeeDetailsThunk.pending, (state) => {
        state.isLoadingDetails = true
        state.error = null
      })
      .addCase(fetchEmployeeDetailsThunk.fulfilled, (state, action) => {
        state.isLoadingDetails = false
        state.employeeDetails = action.payload
      })
      .addCase(fetchEmployeeDetailsThunk.rejected, (state, action) => {
        state.isLoadingDetails = false
        state.error = action.payload ?? "Failed to load employee details"
      })
      .addCase(acceptEmployeeThunk.fulfilled, (state) => {
        if (state.employeeDetails) {
          state.employeeDetails.onboardingStatus = "Approved"
        }
      })
      .addCase(flagEmployeeSectionsThunk.fulfilled, (state) => {
        if (state.employeeDetails) {
          state.employeeDetails.onboardingStatus = "Rejected"
        }
      })
  },
})

export const {
  setEmployees,
  setSelectedEmployee,
  setEmployeesLoading,
  setEmployeesError,
  clearEmployeesError,
  clearEmployeesState,
} = employeeSlice.actions

export default employeeSlice.reducer

export const selectEmployees = createSelector(
  (state: RootState) => state.employees.items,
  (items) =>
    [...items].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    ),
)
export const selectSelectedEmployee = (state: RootState) =>
  state.employees.selectedEmployee
export const selectEmployeesLoading = (state: RootState) =>
  state.employees.isLoading
export const selectEmployeesError = (state: RootState) => state.employees.error
export const selectEmployeeDetails = (state: RootState) =>
  state.employees.employeeDetails
export const selectEmployeeDetailsLoading = (state: RootState) =>
  state.employees.isLoadingDetails
